
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d52hv1l45eoa1ne7l3dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/91f1c86e-2107-4504-b2df-d729ac62f6c3.png" 
              alt="SERVISUR S.R.L. Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" className={`text-sm font-medium transition-colors hover:text-cyan-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Home</a>
            <a href="#about" className={`text-sm font-medium transition-colors hover:text-cyan-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>About</a>
            <a href="#services" className={`text-sm font-medium transition-colors hover:text-cyan-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>VPN Services</a>
            <a href="#features" className={`text-sm font-medium transition-colors hover:text-cyan-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Features</a>
            <a href="#contact" className={`text-sm font-medium transition-colors hover:text-cyan-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
          </div>
          <a 
            href="#contact" 
            className="hidden md:block bg-cyan-500 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Protected
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-purple-900 to-gray-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-3xl">
            <h1 className="text-7xl font-extrabold text-white leading-tight mb-6">
              SECURE YOUR<br />DIGITAL WORLD
            </h1>
            <p className="text-xl text-gray-300 font-light mb-10 max-w-2xl">
              Enterprise-grade VPN protection for your privacy and security across all devices
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-cyan-500 text-gray-900 px-10 py-4 rounded-full text-lg font-semibold hover:bg-cyan-400 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Start Now
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="inline-block bg-cyan-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
                Who We Are
              </div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/96d81550971dd4609c85202971e4ea68.jpg" 
                  alt="Digital Security" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-8">
                PROTECTING YOUR<br />PRIVACY IS OUR<br />MISSION
              </h2>
              <p className="text-gray-700 text-base leading-relaxed mb-6">
                SERVISUR S.R.L. specializes in developing cutting-edge VPN applications that safeguard your online presence. We combine advanced encryption technology with user-friendly design to ensure your digital privacy remains uncompromised.
              </p>
              <p className="text-gray-700 text-base leading-relaxed mb-8">
                Based in Argentina, we serve clients worldwide with our commitment to providing secure, reliable, and fast VPN solutions. Our team of security experts works tirelessly to stay ahead of emerging threats and deliver the protection you deserve.
              </p>
              <button className="bg-gray-900 text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start mb-16">
            <div className="mb-8 md:mb-0">
              <p className="text-gray-500 text-sm uppercase tracking-widest mb-3">Our Services</p>
              <h2 className="text-5xl font-bold text-gray-900 italic">What We Deliver</h2>
            </div>
            <p className="text-gray-600 text-base max-w-md leading-relaxed">
              We provide comprehensive VPN solutions designed to protect your online activities, secure your data, and give you complete peace of mind in the digital world.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="relative rounded-xl overflow-hidden mb-6 h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=Encrypted%20data%20streams%20with%20glowing%20blue%20binary%20code%20flowing%20through%20dark%20digital%20tunnel%2C%20military%20grade%20encryption%20visualization%20with%20lock%20symbols%20and%20secure%20network%20pathways%2C%20cybersecurity%20technology%20concept%20with%20neon%20blue%20accents%20on%20black%20background&width=400&height=300&seq=service-encryption-card&orientation=landscape" 
                  alt="Military-Grade Encryption" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="inline-block bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                Core Service
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                Military-Grade Encryption
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Advanced AES-256 encryption protects your data from unauthorized access and ensures complete privacy for all your online activities.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="relative rounded-xl overflow-hidden mb-6 h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=Neon%20cyan%20glowing%20lock%20icon%20on%20dark%20background%20with%20digital%20circuit%20patterns%2C%20cybersecurity%20shield%20symbol%20with%20geometric%20network%20connections%2C%20modern%20technology%20security%20concept%20with%20bright%20blue%20luminous%20effects%20and%20abstract%20data%20protection%20visualization&width=400&height=300&seq=service-network-card&orientation=landscape" 
                  alt="Global Server Network" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="inline-block bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                Core Service
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                Global Server Network
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Access our worldwide network of secure servers for fast, reliable connections wherever you are, ensuring optimal performance and security.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="relative rounded-xl overflow-hidden mb-6 h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=Global%20network%20visualization%20with%20glowing%20blue%20connection%20lines%20across%20world%20map%2C%20international%20server%20network%20with%20purple%20and%20cyan%20nodes%2C%20digital%20connectivity%20concept%20showing%20worldwide%20data%20flow%20and%20secure%20communication%20pathways%20on%20dark%20background&width=400&height=300&seq=service-policy-card&orientation=landscape" 
                  alt="Zero-Log Policy" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="inline-block bg-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
                Core Service
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">
                Zero-Log Policy
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We never track, collect, or store your online activities. Your privacy is guaranteed with our strict no-logging policy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-6xl font-bold leading-tight">
              <span className="text-gray-900">POWERFUL FEATURES</span><br />
              <span className="text-cyan-500">FOR COMPLETE PROTECTION</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="md:row-span-2 relative rounded-2xl overflow-hidden shadow-xl h-96 md:h-full">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20VPN%20application%20interface%20dashboard%20with%20dark%20theme%2C%20sleek%20user%20interface%20showing%20connection%20status%20and%20server%20selection%2C%20digital%20security%20control%20panel%20with%20blue%20and%20purple%20gradient%20accents%2C%20professional%20software%20design%20with%20clean%20typography%20and%20intuitive%20layout%20elements&width=600&height=800&seq=feature-dashboard-large&orientation=portrait" 
                alt="VPN Dashboard Interface" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-6 left-6 bg-gray-900/80 backdrop-blur-sm px-4 py-2 rounded-lg">
                <p className="text-white font-semibold text-sm">Intuitive Dashboard</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl h-48">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-purple-600 flex items-center justify-center">
                <img 
                  src="https://readdy.ai/api/search-image?query=Digital%20speed%20meter%20graphic%20with%20glowing%20cyan%20and%20purple%20gradient%2C%20velocity%20gauge%20showing%20high%20performance%20metrics%2C%20modern%20speedometer%20visualization%20with%20neon%20effects%20and%20dynamic%20motion%20blur%2C%20technology%20performance%20indicator%20on%20gradient%20background&width=600&height=300&seq=feature-speed-card&orientation=landscape" 
                  alt="High Speed Connection" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                <p className="text-gray-900 font-semibold text-xs">Lightning Fast</p>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl h-48 bg-gray-900 flex items-center justify-center">
              <img 
                src="https://readdy.ai/api/search-image?query=Multi%20device%20compatibility%20icons%20on%20dark%20navy%20background%2C%20smartphone%20tablet%20laptop%20and%20desktop%20computer%20symbols%20in%20white%2C%20cross%20platform%20technology%20illustration%20showing%20device%20synchronization%2C%20minimalist%20icon%20set%20for%20universal%20application%20support&width=600&height=300&seq=feature-devices-card&orientation=landscape" 
                alt="Multi-Device Support" 
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-cyan-500 px-3 py-1.5 rounded-lg">
                <p className="text-white font-semibold text-xs">All Devices</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20digital%20network%20with%20blue%20and%20purple%20gradient%20overlay%2C%20panoramic%20wide%20view%20of%20interconnected%20nodes%20and%20data%20pathways%2C%20futuristic%20cybersecurity%20background%20with%20glowing%20connection%20lines%20and%20geometric%20patterns%2C%20dark%20technological%20landscape%20with%20neon%20accents%20representing%20secure%20communication%20infrastructure&width=1400&height=600&seq=cta-background-wide&orientation=landscape" 
            alt="Digital Network Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/90 to-gray-900/90"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <h2 className="text-7xl font-bold text-white lowercase mb-8 md:mb-0">
              get started<br />today
            </h2>
            <div className="text-right">
              <p className="text-xl text-white font-light mb-6 leading-relaxed">
                Join thousands protecting their digital privacy<br />
                Experience true online freedom
              </p>
              <a 
                href="#contact" 
                className="inline-flex items-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Start Protection
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <p className="text-cyan-500 text-xs uppercase tracking-widest mb-3 font-semibold">Get In Touch</p>
              <h2 className="text-5xl font-bold text-gray-900 mb-6">Let's Connect</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-12">
                Have questions about our VPN services? Want to learn more about how we can protect your digital privacy? Reach out to us and our team will be happy to assist you.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <button 
                  type="submit" 
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-cyan-500 text-white px-8 py-3.5 rounded-lg text-base font-semibold hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your message has been sent successfully.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Sorry, there was an error sending your message. Please try again.
                  </div>
                )}
              </form>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 font-semibold">Location</p>
                <p className="text-gray-900 text-base leading-relaxed">
                  AVELLANEDA 55<br />
                  Choele Choel, R8360DSA<br />
                  Argentina
                </p>
              </div>

              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 font-semibold">Phone</p>
                <p className="text-gray-900 text-base mb-8">+1 5674852809</p>

                <p className="text-gray-500 text-xs uppercase tracking-wider mb-3 font-semibold">Email</p>
                <a href="mailto:support@servisursrl.net" className="text-cyan-500 text-base hover:text-cyan-600 transition-colors cursor-pointer">
                  support@servisursrl.net
                </a>
              </div>

              <div className="md:col-span-2">
                <div className="relative rounded-xl overflow-hidden h-64 shadow-lg">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Modern%20technology%20office%20environment%20with%20sleek%20workstations%20and%20blue%20ambient%20lighting%2C%20professional%20workspace%20with%20computers%20and%20digital%20displays%2C%20contemporary%20business%20setting%20with%20minimalist%20design%20and%20technological%20atmosphere%2C%20clean%20corporate%20interior%20with%20glass%20and%20metal%20elements&width=600&height=400&seq=contact-office-image&orientation=landscape" 
                    alt="Office Environment" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="md:col-span-1">
              <h3 className="text-6xl font-bold mb-6">SERVISUR</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-sm">
                Leading VPN service provider committed to protecting your digital privacy with cutting-edge encryption technology and a strict no-logging policy. Your security is our priority.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-all cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-all cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-all cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="#" className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center hover:bg-white hover:text-black transition-all cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>

            <div>
              <p className="text-gray-500 text-xs uppercase tracking-widest mb-4 font-semibold">Address</p>
              <p className="text-white text-sm leading-relaxed mb-12">
                AVELLANEDA 55<br />
                Choele Choel, R8360DSA<br />
                Argentina
              </p>

              <p className="text-gray-500 text-xs uppercase tracking-widest mb-4 font-semibold">Phone</p>
              <p className="text-white text-sm mb-12">+1 5674852809</p>

              <p className="text-gray-500 text-xs uppercase tracking-widest mb-4 font-semibold">Email</p>
              <a href="mailto:support@servisursrl.net" className="text-white text-sm hover:text-cyan-500 transition-colors cursor-pointer">
                support@servisursrl.net
              </a>
            </div>

            <div>
              <div className="relative rounded-xl overflow-hidden h-64 shadow-lg">
                <img 
                  src="https://readdy.ai/api/search-image?query=Futuristic%20technology%20workspace%20with%20blue%20neon%20lighting%20and%20digital%20displays%2C%20modern%20cybersecurity%20operations%20center%20with%20multiple%20monitors%2C%20high%20tech%20office%20environment%20with%20ambient%20purple%20and%20cyan%20glow%2C%20professional%20security%20command%20center%20with%20sleek%20contemporary%20design&width=500&height=400&seq=footer-tech-image&orientation=portrait" 
                  alt="Technology Workspace" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-xs">
              © 2025 SERVISUR S.R.L. All rights reserved
            </p>
            <div className="flex gap-8">
              <a href="#" className="text-white text-xs hover:text-cyan-500 transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</a>
              <a href="#" className="text-white text-xs hover:text-cyan-500 transition-colors cursor-pointer whitespace-nowrap">Terms</a>
              <a href="#" className="text-white text-xs hover:text-cyan-500 transition-colors cursor-pointer whitespace-nowrap">Security</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-white text-xs hover:text-cyan-500 transition-colors cursor-pointer whitespace-nowrap">Website Builder</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
